import { useEffect, useRef, useState } from "react";
import useIsMobile from "../hooks/useIsMobile";
import RecommendationCard from "./recommendation-card/RecommendationCard";
import { RecommendationData } from "ta-agent-sdk";
import { useMaps } from "../contexts/MapsContext";
import { useTranslation } from "react-i18next";

export function RecommendationList({
  recommendations,
}: {
  recommendations: RecommendationData[];
}) {
  const { t } = useTranslation();
  const { currentTile } = useMaps();
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const isAutoScrollingRef = useRef(false);
  const isMobile = useIsMobile();
  // Update start/end state for the current axis
  const updateScrollButtons = () => {
    const el = scrollerRef.current;
    if (!el) return;
    if (isMobile) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setAtStart(scrollLeft <= 8);
      setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
    } else {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setAtStart(scrollTop <= 8);
      setAtEnd(scrollTop + clientHeight >= scrollHeight - 1);
    }
  };

  const getItems = () => {
    const el = scrollerRef.current;
    if (!el) return [] as HTMLElement[];
    return Array.from(
      el.querySelectorAll(':scope > [data-snap-item="true"]'),
    ) as HTMLElement[];
  };

  const getCurrentVisibleItemIndex = () => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const children = getItems();
    if (!children.length) return 0;
    const containerRect = el.getBoundingClientRect();
    const containerCenter = isMobile
      ? containerRect.left + containerRect.width / 2
      : containerRect.top + containerRect.height / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    children.forEach((child, index) => {
      const childRect = child.getBoundingClientRect();
      const childCenter = isMobile
        ? childRect.left + childRect.width / 2
        : childRect.top + childRect.height / 2;
      const distance = Math.abs(childCenter - containerCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const scrollToItem = (index: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const children = getItems();
    const targetChild = children[index];
    if (!targetChild) return;

    const containerRect = el.getBoundingClientRect();
    const childRect = targetChild.getBoundingClientRect();
    // gap-2 => 8px visual offset
    const gapOffset = 8;

    if (isMobile) {
      const scrollLeft =
        el.scrollLeft +
        (childRect.left - containerRect.left) -
        (containerRect.width - childRect.width) / 2 +
        gapOffset;
      isAutoScrollingRef.current = true;
      el.scrollTo({ left: scrollLeft, behavior: "smooth" });
    } else {
      const scrollTop =
        el.scrollTop +
        (childRect.top - containerRect.top) -
        (containerRect.height - childRect.height) / 2 +
        gapOffset;
      isAutoScrollingRef.current = true;
      el.scrollTo({ top: scrollTop, behavior: "smooth" });
    }

    // Reset the flag after scroll animation completes
    window.setTimeout(() => {
      isAutoScrollingRef.current = false;
      updateScrollButtons();
    }, 500);
  };

  const scrollByDir = (dir: "prev" | "next") => {
    const el = scrollerRef.current;
    if (!el) return;

    if (isMobile) {
      const currentIndex = getCurrentVisibleItemIndex();
      const targetIndex =
        dir === "next"
          ? Math.min(currentIndex + 1, recommendations.length - 1)
          : Math.max(currentIndex - 1, 0);
      scrollToItem(targetIndex);
    } else {
      const amount = el.clientHeight * 0.6;
      el.scrollBy({
        top: dir === "next" ? amount : -amount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    updateScrollButtons();
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => {
      updateScrollButtons();
      // Auto-snap only on mobile (horizontal)
      if (isMobile && !isAutoScrollingRef.current) {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = window.setTimeout(() => {
          if (!isAutoScrollingRef.current) {
            const currentIndex = getCurrentVisibleItemIndex();
            isAutoScrollingRef.current = true;
            scrollToItem(currentIndex);
          }
        }, 100);
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    const onResize = () => updateScrollButtons();
    window.addEventListener("resize", onResize);

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateScrollButtons());
      ro.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", onScroll as EventListener);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
    // Re-run when axis changes or list length changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, recommendations.length]);

  // When the map's currentTile changes, scroll that card into view
  useEffect(() => {
    if (!currentTile) return;

    const getId = (r: RecommendationData) =>
      "location" in r ? r.location.id : r.experience.id;

    let index = recommendations.findIndex(
      (r) => getId(r) === getId(currentTile),
    );

    // Fallback: try to match by coordinates if id not found
    if (index === -1) {
      const getCoords = (r: RecommendationData) =>
        "location" in r && r.location.coordinates
          ? [r.location.coordinates.longitude, r.location.coordinates.latitude]
          : "experience" in r && r.experience.coordinates
            ? [
                r.experience.coordinates.longitude,
                r.experience.coordinates.latitude,
              ]
            : null;

      const p = getCoords(currentTile);
      if (!p) return;
      const [clng, clat] = p;
      index = recommendations.findIndex((r) => {
        const p2 = getCoords(r);
        if (!p2) return false;
        const [lng, lat] = p2;
        return lng === clng && lat === clat;
      });
    }

    if (index >= 0) {
      // Defer to ensure DOM has rendered the list
      requestAnimationFrame(() => scrollToItem(index));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTile, recommendations, isMobile]);
  return (
    <>
      {recommendations.length > 0 && (
        <div className="absolute right-0 top-auto bottom-[140px] md:bottom-auto md:top-0 h-auto w-full md:w-auto md:h-full z-[99] pointer-events-auto">
          <div className="flex flex-col h-auto w-full md:w-auto md:h-full relative">
            <section
              ref={scrollerRef}
              className="flex flex-row md:flex-col gap-2 z-20 shrink-0 w-full py-4 px-5 h-full rounded-lg scrollbar-hidden overflow-x-auto overflow-y-hidden md:overflow-x-hidden md:overflow-y-auto"
            >
              {/* Gradients: horizontal on mobile, vertical on desktop */}
              {recommendations.length > 1 &&
                !atStart &&
                (isMobile ? (
                  <div className="pointer-events-none absolute left-0 top-0 bottom-0 my-auto h-[calc(100%-32px)] w-8 bg-gradient-to-r rounded-none from-black/20 to-transparent z-20" />
                ) : (
                  <div className="pointer-events-none absolute left-0 top-0 right-0 mx-auto w-[350px] h-8 bg-gradient-to-b from-black/20 to-transparent z-20" />
                ))}
              {recommendations.length > 1 &&
                !atEnd &&
                (isMobile ? (
                  <div className="pointer-events-none absolute right-0 top-0 bottom-0 my-auto h-[calc(100%-32px)] w-8 bg-gradient-to-l rounded-none from-black/20 to-transparent z-20" />
                ) : (
                  <div className="pointer-events-none absolute left-0 bottom-0 right-0 mx-auto w-[350px] h-8 bg-gradient-to-t from-black/20 to-transparent z-20" />
                ))}
              {/* Arrow buttons: left/right on mobile, up/down on desktop */}
              {recommendations.length > 1 &&
                !atStart &&
                (isMobile ? (
                  <button
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white shadow-md rounded-full p-2 hover:scale-110 text-black"
                    aria-label={t("recommendationList.scrollLeft")}
                    onClick={() => scrollByDir("prev")}
                  >
                    <svg
                      width="24"
                      height="24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    className="absolute left-1/2 -translate-x-1/2 top-2 z-20 bg-white shadow-md rounded-full p-2 hover:scale-110 text-black"
                    aria-label={t("recommendationList.scrollUp")}
                    onClick={() => scrollByDir("prev")}
                  >
                    <svg
                      width="24"
                      height="24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 15l-7-7-7 7" />
                    </svg>
                  </button>
                ))}
              {recommendations.map((recommendation, index) => (
                <RecommendationCard
                  key={`${
                    "location" in recommendation
                      ? recommendation.location.id
                      : recommendation.experience.id
                  }-${index}`}
                  data-snap-item="true"
                  recommendation={recommendation}
                />
              ))}
              {recommendations.length > 1 &&
                !atEnd &&
                (isMobile ? (
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white shadow-md rounded-full p-2 hover:scale-110 text-black"
                    aria-label={t("recommendationList.scrollRight")}
                    onClick={() => scrollByDir("next")}
                  >
                    <svg
                      width="24"
                      height="24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    className="absolute left-1/2 -translate-x-1/2 bottom-2 z-20 bg-white shadow-md rounded-full p-2 hover:scale-110 text-black"
                    aria-label={t("recommendationList.scrollDown")}
                    onClick={() => scrollByDir("next")}
                  >
                    <svg
                      width="24"
                      height="24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M5 9l7 7 7-7" />
                    </svg>
                  </button>
                ))}
            </section>
          </div>
        </div>
      )}
    </>
  );
}
